/** Memory-only opening reads; mutations retire an entire generation so old requests cannot repopulate it. */
import {runWarmTasks} from '../../services/mainScreenWarm';
import {OutreachService} from '../../services/OutreachService';
import {backgroundReadKey, currentBackgroundReadScope, forgetBackgroundRead, peekBackgroundRead, readInBackground, type BackgroundReadScope} from '../../services/BackgroundReadCache';
import type {BatchSummary} from '../../types/outreach';

type Read<T> = {name: string; load: () => Promise<T>};
const generations = new Map<string, number>();
const key = (scope: BackgroundReadScope, name: string) => backgroundReadKey(scope, name);
const nameFor = (scope: BackgroundReadScope, name: string) => `${name}/opening-${generations.get(key(scope, name)) ?? 0}`;
export function invalidateOutreachRead(workspaceId: string, name: string): void {
  const scope = currentBackgroundReadScope(workspaceId);
  if (!scope) return;
  forgetBackgroundRead(scope, nameFor(scope, name));
  generations.set(key(scope, name), (generations.get(key(scope, name)) ?? 0) + 1);
}
export function peekOutreachRead<T>(workspaceId: string, read: Read<T>): T | undefined {
  const scope = currentBackgroundReadScope(workspaceId);
  return scope ? peekBackgroundRead<T>(scope, nameFor(scope, read.name))?.data : undefined;
}
export async function loadOutreachRead<T>(workspaceId: string, read: Read<T>, {maxAgeMs = 0}: {maxAgeMs?: number} = {}): Promise<T> {
  const scope = currentBackgroundReadScope(workspaceId);
  if (!scope) return read.load();
  const name = nameFor(scope, read.name);
  const result = await readInBackground(scope, name, read.load, {maxAgeMs, persist: false});
  if (currentBackgroundReadScope(workspaceId)?.actorId !== scope.actorId || nameFor(scope, read.name) !== name) throw new Error('Outreach read scope changed.');
  return result;
}
function scopedRows<T extends {workspaceId: string}>(workspaceId: string, rows: T[]): T[] {
  if (!Array.isArray(rows) || rows.some(row => row?.workspaceId !== workspaceId)) throw new Error('Outreach data scope could not be confirmed.');
  return rows;
}
export const pendingBatchesRead = (workspaceId: string): Read<BatchSummary[]> => ({
  name: 'outreach/approvals',
  load: async () => {
    const response = await OutreachService.listBatches(workspaceId, {status: 'pending'});
    const rows = Array.isArray(response) ? response : (response as {batches?: BatchSummary[]})?.batches;
    return scopedRows(workspaceId, rows as BatchSummary[]).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  },
});
export const templatesRead = (workspaceId: string) => ({name: 'outreach/templates', load: async () => scopedRows(workspaceId, await OutreachService.listTemplates(workspaceId))});
export const settingsRead = (workspaceId: string) => ({name: 'outreach/settings', load: async () => {
  const result = await OutreachService.getSettings(workspaceId);
  if (result && result.workspaceId !== workspaceId) throw new Error('Outreach settings scope could not be confirmed.');
  return result;
}});
export const suppressionsRead = (workspaceId: string) => ({name: 'outreach/suppressions', load: async () => {
  const result = await OutreachService.listSuppressions(workspaceId, {limit: 200});
  scopedRows(workspaceId, result.suppressions);
  return result;
}});
export async function warmOutreachSections(workspaceId: string, maxAgeMs = 30_000): Promise<void> {
  const owner = currentBackgroundReadScope(workspaceId)?.actorId;
  if (!owner) return;
  const reads = [pendingBatchesRead(workspaceId), templatesRead(workspaceId), settingsRead(workspaceId), suppressionsRead(workspaceId)];
  await runWarmTasks(reads.map(read => () => loadOutreachRead<unknown>(workspaceId, read, {maxAgeMs})), () => currentBackgroundReadScope(workspaceId)?.actorId === owner);
}
